import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
  Tooltip,
} from '@mui/material';
import { isEmpty, map } from 'es-toolkit/compat';
import React from 'react';

export interface LazyLoadSelectOption {
  value: string;
  label: React.ReactNode;
  menuContent?: React.ReactNode;
}

interface LazyLoadSelectProps extends Omit<SelectProps, 'onScroll' | 'label'> {
  label: string;
  options: LazyLoadSelectOption[];
  loadMoreOptions: () => void;
  isLoading: boolean;
  helperText?: string;
  formControlProps?: React.ComponentProps<typeof FormControl>;
  loadingTitle?: string;
}

const LazyLoadSelect: React.FC<LazyLoadSelectProps> = ({
  label,
  options,
  loadMoreOptions,
  isLoading,
  helperText,
  formControlProps,
  loadingTitle,
  ...props
}) => {
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const bottom =
      event.currentTarget.scrollHeight === event.currentTarget.scrollTop + event.currentTarget.clientHeight;
    if (bottom && !isLoading) {
      loadMoreOptions();
    }
  };

  return (
    <FormControl fullWidth required={props.required} {...formControlProps}>
      <InputLabel error={props.error}>{label}</InputLabel>
      <Select
        {...props}
        label={label}
        endAdornment={
          isLoading ? (
            <InputAdornment position="end" sx={{ marginRight: 6 }}>
              <Tooltip title={loadingTitle || 'Loading...'}>
                <CircularProgress color="inherit" size={20} />
              </Tooltip>
            </InputAdornment>
          ) : (
            props?.endAdornment
          )
        }
        MenuProps={{
          PaperProps: {
            onScroll: handleScroll,
            style: { maxHeight: 200, overflowY: 'auto' },
          },
        }}
        variant="outlined"
      >
        {map(options, (option, index) => (
          <MenuItem key={index} value={option?.value}>
            {option?.menuContent || option?.label}
          </MenuItem>
        ))}
        {isLoading && (
          <MenuItem disabled>
            Loading...
            <CircularProgress size={24} />
          </MenuItem>
        )}
        {isEmpty(options) && !isLoading && <MenuItem disabled>No options available</MenuItem>}
      </Select>
      <FormHelperText error={props.error}>{helperText}</FormHelperText>
    </FormControl>
  );
};

export default LazyLoadSelect;
